import { useParams } from "react-router-dom";

import { LoadScreen, PageBackButton } from "@/core/components/shared";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/core/components/ui/tabs";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { useSavingsVault } from "@/features/savings/api/use-savings-vault";
import type { SavingsVaultStatus } from "@/features/savings/api/enums";
import {
  VaultActions,
  VaultBalanceCard,
  VaultLockEventsSection,
  VaultMovementsSection,
} from "@/features/savings/components";

const STATUS_VARIANTS: Record<SavingsVaultStatus, "success" | "secondary"> = {
  OPEN: "success",
  CLOSED: "secondary",
};

const STATUS_LABEL_KEYS = {
  OPEN: "savings.enums.vaultStatus.OPEN",
  CLOSED: "savings.enums.vaultStatus.CLOSED",
} as const satisfies Record<SavingsVaultStatus, string>;

/**
 * Détail d'un coffre d'épargne (`GET /SavingsVaults/{id}`, guide v3.1
 * §6.10) — soldes + progression + blocage, actions (versement / retrait /
 * blocage / renommage / clôture / déblocage anticipé maker-checker) et
 * onglets mouvements / blocages. Route cible :
 * `/app/wallet/savings-vaults/:id` (câblage router/nav : phase 18.7).
 */
export function SavingsVaultDetailPage() {
  const { t } = useTypedTranslation();
  const params = useParams();
  const vaultId = params.id ?? params.vaultId ?? "";
  const { data: vault, isLoading, error, refetch } = useSavingsVault(vaultId);

  const backButton = (
    <PageBackButton to="/app/wallet/savings-vaults" label={t("common.back")} />
  );

  if (isLoading) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex h-64 items-center justify-center">
          <LoadScreen />
        </div>
      </section>
    );
  }

  if (error || !vault) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex flex-col items-center gap-4 py-24">
          <p className="font-semibold text-destructive">
            {error?.message ?? t("common.error")}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      </section>
    );
  }

  const fields: { label: string; value?: string }[] = [
    { label: t("savings.vaults.detail.id"), value: vault.id },
    {
      label: t("savings.vaults.detail.ownerKeycloakUserId"),
      value: vault.ownerKeycloakUserId ?? undefined,
    },
    {
      label: t("savings.vaults.detail.vaultWalletId"),
      value: vault.vaultWalletId ?? undefined,
    },
    {
      label: t("savings.vaults.columns.currency"),
      value: vault.currency,
    },
    {
      label: t("savings.vaults.detail.createdAt"),
      value: vault.createdAt
        ? formatDateWithFallback(vault.createdAt, "—")
        : undefined,
    },
    {
      label: t("savings.vaults.detail.closedAt"),
      value: vault.closedAt
        ? formatDateWithFallback(vault.closedAt, "—")
        : undefined,
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {backButton}
          <h1 className="text-2xl font-semibold text-foreground">
            <NoTranslate as="span">{vault.name || vault.id}</NoTranslate>
          </h1>
        </div>
        <Badge variant={STATUS_VARIANTS[vault.status] ?? "outline"}>
          {t(STATUS_LABEL_KEYS[vault.status])}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("savings.vaults.detail.infoTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map(({ label, value }) => (
              <div key={label}>
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd className="font-medium">
                  <NoTranslate as="span" className="break-all">
                    {value || "—"}
                  </NoTranslate>
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <VaultBalanceCard vault={vault} />

      <VaultActions vault={vault} />

      <Tabs defaultValue="movements" className="w-full">
        <TabsList className="flex w-full flex-wrap">
          <TabsTrigger value="movements">
            {t("savings.vaults.tabs.movements")}
          </TabsTrigger>
          <TabsTrigger value="lock-events">
            {t("savings.vaults.tabs.lockEvents")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="movements" className="mt-4">
          <VaultMovementsSection vaultId={vaultId} />
        </TabsContent>
        <TabsContent value="lock-events" className="mt-4">
          <VaultLockEventsSection vaultId={vaultId} />
        </TabsContent>
      </Tabs>
    </section>
  );
}
